/**
 * 🧪✨ RetroMacroBackfill — inline "Estimate Macros" button for a Quick Log
 * entry that was logged with calories only. Tapping the button hits
 * `POST /api/vitals/quick/{entry_id}/estimate-macros` (Gemini text-only)
 * for a preview, then opens a confirmation modal so the user reviews the
 * numbers before committing via `PATCH /api/vitals/quick/{entry_id}`.
 *
 * Feb 2026 canon: Snap-a-Meal already fills macros on new entries — this
 * component is the backfill path for old calorie-only entries.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RetroMacroBackfill({ entry, onSaved, className = "" }) {
  const [busy,   setBusy]   = useState(false);
  const [modal,  setModal]  = useState(null); // { estimate: {...}, confidence, notes }
  const [saving, setSaving] = useState(false);

  const requestEstimate = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/vitals/quick/${entry.id}/estimate-macros`);
      setModal(r.data?.estimate ? r.data : null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI estimate failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!modal?.estimate || saving) return;
    setSaving(true);
    try {
      const est = modal.estimate;
      const r = await axios.patch(`${API}/vitals/quick/${entry.id}`, {
        protein_g: est.protein_g,
        carbs_g:   est.carbs_g,
        fat_g:     est.fat_g,
        fiber_g:   est.fiber_g,
        sugar_g:   est.sugar_g,
        sodium_mg: est.sodium_mg,
      });
      toast.success(`✨ Macros filled — P${est.protein_g}g · C${est.carbs_g}g · F${est.fat_g}g`);
      setModal(null);
      onSaved?.(r.data?.entry);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={requestEstimate}
        disabled={busy}
        data-testid={`vitals-quick-estimate-${entry.id}`}
        title="AI-estimate P/C/F/fiber/sugar/sodium for this entry"
        className={`inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 disabled:opacity-40 transition ${className}`}
      >
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
        <span className="text-[9px] uppercase tracking-widest font-bold">
          {busy ? "Estimating…" : "Estimate"}
        </span>
      </button>

      {modal && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !saving && setModal(null)}
          data-testid="retro-macro-modal"
        >
          <div
            className="bg-[#0F0F0F] border-2 border-emerald-500/50 max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-bold flex items-center gap-1">
                  <Sparkles size={11} /> AI Macro Estimate
                </div>
                <h3 className="font-display text-lg text-white mt-1 leading-tight">{modal.name}</h3>
                <div className="text-[10px] text-neutral-500 mt-0.5">
                  {modal.calories ? `${modal.calories} kcal · ` : ""}
                  <span className="text-emerald-300/80 uppercase tracking-widest">
                    confidence: {modal.estimate?.confidence || "medium"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setModal(null)}
                disabled={saving}
                className="text-neutral-500 hover:text-white disabled:opacity-40"
                data-testid="retro-macro-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-1.5 mb-3">
              <MacroCell label="Protein" value={`${modal.estimate.protein_g}g`}     tone="text-pink-300" />
              <MacroCell label="Carbs"   value={`${modal.estimate.carbs_g}g`}       tone="text-sky-300" />
              <MacroCell label="Fat"     value={`${modal.estimate.fat_g}g`}         tone="text-amber-300" />
              <MacroCell label="Fiber"   value={`${modal.estimate.fiber_g}g`}       tone="text-emerald-300" />
              <MacroCell label="Sugar"   value={`${modal.estimate.sugar_g}g`}       tone="text-orange-300" />
              <MacroCell label="Sodium"  value={`${modal.estimate.sodium_mg}mg`}    tone="text-rose-300" />
            </div>

            {modal.estimate?.notes && (
              <p className="text-[10px] italic text-neutral-500 mb-3 leading-relaxed">
                💭 {modal.estimate.notes}
              </p>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => setModal(null)}
                disabled={saving}
                data-testid="retro-macro-cancel"
                className="flex-1 py-2 border border-zinc-700 text-neutral-400 hover:border-zinc-500 text-xs uppercase tracking-widest transition disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={commit}
                disabled={saving}
                data-testid="retro-macro-save"
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs uppercase tracking-widest font-bold transition disabled:opacity-40 flex items-center justify-center gap-1"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {saving ? "Saving…" : "Fill Macros"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MacroCell({ label, value, tone }) {
  return (
    <div className="bg-black/60 border border-zinc-800 py-1.5 px-2 text-center">
      <div className={`font-mono tabular-nums text-base font-bold ${tone}`}>{value}</div>
      <div className="text-[8px] uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}
